/**
 * SupportItemPage — detail page at `/support/:id`.
 *
 * Web port of Flutter's `SupportItemScreen`
 * (`lib/presentation/web/support_item_screen.dart:1-105`). Renders the
 * selected item's title in the centered app bar above a shadow card that
 * contains either the item's description (default) or a tappable "Open
 * Privacy Policy" link (when `category === SupportCategory.privacyPolicy`).
 *
 * Same lifecycle / screen-key / app-bar pattern as `LegalDocumentPage.tsx`,
 * with the bar rendered by the shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`).
 *
 * App-bar provenance: the ported `lib/presentation/web/support_item_screen.dart`
 * has **no** app bar at all — it renders the item title as a *body* heading
 * (`:47-51`) — so the bar is a pre-existing web-only addition
 * (`workflow/lessons.md` — a full-page web route needs a back affordance or a
 * deep-linked user is stranded). Its shape follows the *mobile* WebView screen
 * `lib/presentation/screens/legal/support_item_screen.dart:106-123` (back
 * leading + centered title), a different screen from the ported one — and
 * that screen hardcodes "Support" as its bar title, so putting the *item's*
 * title in the bar is a further web addition. Neither is a Flutter-parity
 * claim for the bar itself.
 *
 * Out-of-range handling: when `id` is `NaN`, negative, non-integer, or
 * `>= items.length`, the body renders nothing — parity with Flutter's empty
 * `Container` at `support_item_screen.dart:27-30` (which returns that
 * `Container` for the *whole* screen; there is no bar in that file to keep).
 * The app bar still renders with the back button so the user has a way out —
 * a deliberate web addition under the same `workflow/lessons.md` rule, which
 * requires the back affordance in **every** screen state — and the title
 * falls back to the page-level "Support" label.
 *
 * Moving from the hand-rolled bar to `ExpauseAppBar` makes three intentional
 * visual changes: the title steps from `responsive.font.lg`/`semibold` to
 * `responsive.font.xl`/`bold`, the bar becomes sticky with a blurred
 * 50%-opacity backdrop, and the top safe-area inset is now honoured.
 *
 * **Web deviation from Flutter (documented per `presentation.md`):**
 *
 * - For `SupportCategory.privacyPolicy`, Flutter calls
 *   `NavigationService.navigateToPrivacyPolicyScreen()` immediately followed
 *   by `NavigationService.pop()` (`support_item_screen.dart:78-81`). The web
 *   intentionally **does not** call `Navigator.goBack()` after the navigate.
 *   On the web, `pop()` would try to navigate back from the freshly-loaded
 *   privacy-policy page to the support detail page that just routed away —
 *   the opposite of what the user expects from a "go to privacy policy"
 *   link. Web users stay on the privacy policy and can use the browser's
 *   back arrow to return.
 * - The shadow-card style is inlined here (not extracted into a shared
 *   `SupportCard.tsx`) because the duplicated markup with
 *   `SupportCategoryCard` is well under ~15 lines — the plan authorises
 *   inline duplication below that threshold.
 *
 * Accessible without authentication; mounted under `PUBLIC_ROUTES`.
 */
import { useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { Navigator } from '@/presentation/routing/Navigator';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { ExpauseButton, ButtonVariant } from '@/presentation/components/common/controls/ExpauseButton';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { SupportCategory } from '@/domain/enums/support/supportCategory';
import { useSupportItems } from './hooks/useSupportItems';
import { qaAttr } from '@/presentation/utils/qaAttr';

export const SupportItemPage = () => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();
    const { l10n } = useLocale();
    const location = useLocation();

    // Canonical screen-key wiring (mirrors `LegalDocumentPage` /
    // `SupportPage`). Without a screen key the `KeepAliveOutlet` cannot
    // disambiguate two pushed instances of the same detail page.
    const { currentScreenKeyRef } = useKeepAlive();
    const screenKeyRef = useRef<string>(
        currentScreenKeyRef.current || generateScreenKey(location.pathname),
    );
    const screenKey = screenKeyRef.current;

    useBasePage({ screenKey, onInit: () => { } });

    const items = useSupportItems();

    // Resolve the route param. Flutter receives `int? supportItemId` from the
    // router (`support_item_screen.dart:10`); the web parses the string form
    // and validates the integer bounds. Anything outside `[0, items.length)`
    // — including `NaN` and negative values — collapses to a `null` item and
    // renders an empty body (Flutter parity with `:27-30`).
    const idParam = useParams<{ id: string }>().id;
    const id = Number(idParam);
    const isValidId =
        Number.isInteger(id) && id >= 0 && id < items.length;
    const item = isValidId ? items[id] : null;

    const titleText = item ? item.title : l10n.support.title;

    // Deep-link guard: with no in-app history, fall back to the
    // support list (same pattern as `LegalDocumentPage.tsx`) so the
    // user lands somewhere sensible inside the app.
    const handleBack = () => Navigator.goBackOr(() => Navigator.navigateToSupport());

    return (
        <div
            {...qaAttr({ id: 'support-item', status: 'ready' })}
            style={{
                minHeight: '100vh',
                backgroundColor: theme.background,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* App bar — back button + centered title. Deliberately OUTSIDE the
                `{item && …}` branch below so an out-of-range `:id` still
                renders a way out (`workflow/lessons.md`). */}
            <ExpauseAppBar
                rootQa={{ id: 'support-item-app-bar' }}
                title={titleText}
                leadingQa={{ id: 'support-item-back' }}
                onLeadingTap={handleBack}
            />

            {/* Body. When `item` is null, render nothing — parity with
                Flutter's empty `Container` at `support_item_screen.dart:27-30`.
                The app bar above still provides a back button. */}
            {item && (
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: `${responsive.spacing.lg}px`,
                    }}
                >
                    <div
                        style={{
                            width: '100%',
                            maxWidth: responsive.dialog.maxWidth,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Shadow card — same shell as `SupportCategoryCard`.
                            Inlined (not extracted) because the duplicated
                            markup is under the ~15-line extraction
                            threshold the plan calls out. */}
                        <div
                            style={{
                                background: theme.surface,
                                borderRadius: responsive.borderRadius.base,
                                boxShadow: `0 0 ${responsive.spacing.xs}px ${theme.shadow}`,
                                padding: responsive.spacing.lg,
                                width: '100%',
                            }}
                        >
                            {item.category === SupportCategory.privacyPolicy ? (
                                <ExpauseButton
                                    {...qaAttr({ id: 'support-item-privacy-policy' })}
                                    variant={ButtonVariant.text}
                                    onClick={() => Navigator.navigateToPrivacyPolicy()}
                                    style={{
                                        fontSize: responsive.font.base,
                                        fontWeight: responsive.fontWeight.medium,
                                        textAlign: 'left',
                                    }}
                                >
                                    {l10n.support.openPrivacyPolicyLink}
                                </ExpauseButton>
                            ) : (
                                <p
                                    style={{
                                        margin: 0,
                                        color: theme.text.primary,
                                        fontSize: responsive.font.base,
                                        lineHeight: 1.6,
                                        whiteSpace: 'pre-wrap',
                                        textAlign: 'left',
                                    }}
                                >
                                    {item.description}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
